import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AlloyButton, FilterIcon, ModalContext } from 'cat-ecommerce-alloy';
import { useTranslation } from 'react-i18next';
import { useAnalytics, useBreakpoint } from '../../../../hooks';
import {
  setPLPProductCardExpanded,
  setPLPQueryParam
} from '../../../../store/plp/actions';
import usePagination from '../usePagination/usePagination';
import ProductListSpecificationsModalContainer from '../ProductListSpecificationsModalContainer';
import ProductListSpecifications from '@app/components/common/ProductListSpecifications';
import { PAGE_BREAKPOINTS } from '@app/constants/commonConstants';
import { isEmpty } from '@app/utils';

const ProductListSpecificationsContainer = () => {
  const { addModalWithOptions, removeCurrentModal } = useContext(ModalContext);
  const [t] = useTranslation();
  const { dispatchWithImpressionEvent } = useAnalytics();
  const { setUrlToPageOneWithFilter } = usePagination();
  const [isOpen, setIsOpen] = useState(false);
  const currentCategory = useSelector(s => s.plpUI.current);
  const catalogEntries = useSelector(
    s => s.categories.byId[currentCategory]?.productsId
  );
  const expandedId = useSelector(s => s.plpUI.expandedId);
  const orderBy = useSelector(s => s.plpUI.orderBy);
  const activeFacets = useSelector(s => s.plpUI.activeFacets);
  const unitOfMeasure = useSelector(s => s.plpUI.unitOfMeasure);
  const facets = useSelector(
    s => s.categories.byId[currentCategory]?.facets || []
  );
  const showSpecificationsToggle = useSelector(
    s => s.categories.byId[currentCategory]?.showSpecificationsToggle
  );
  const bucketModelNumberFacet = useSelector(
    s => s.categories.byId[currentCategory]?.bucketModelNumber
  );
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);

  useEffect(() => {
    if (isOpen && isMD) {
      removeCurrentModal();
    }
  }, [isMD, isOpen, removeCurrentModal]);
  /**
   * appliedFacets will store the initial active facets value when the
   * modal is opened for the first time. We will dispatch the setPLPQueryParam call
   * only if the modal is closed & appliedFacets and activeFacets are different
   */

  const appliedFacets = React.useRef(activeFacets);
  const restoreInitialActiveFacets = useCallback(() => {
    dispatchWithImpressionEvent(
      setPLPQueryParam({ activeFacets: appliedFacets?.current })
    );
    setUrlToPageOneWithFilter({ facets: appliedFacets?.current });
  }, [dispatchWithImpressionEvent, setUrlToPageOneWithFilter]);

  const isSameActiveFacets =
    activeFacets?.length === appliedFacets?.current?.length &&
    activeFacets?.every((val, index) => val === appliedFacets?.current[index]);

  useEffect(() => {
    if (!isOpen && !isSameActiveFacets && !isMD) {
      restoreInitialActiveFacets();
    }
  }, [isOpen, isMD, isSameActiveFacets, restoreInitialActiveFacets]);

  const showSpecificationsModal = useCallback(() => {
    const title = t('Filter_And_Sort');
    const onOpen = () => setIsOpen(true);
    const onClose = () => {
      setIsOpen(false);
    };

    addModalWithOptions(
      <ProductListSpecificationsModalContainer
        title={title}
        orderBy={orderBy}
        removeCurrentModal={removeCurrentModal}
        appliedFacets={appliedFacets}
        facets={facets}
        bucketModelNumberFacet={bucketModelNumberFacet}
        showSpecificationsToggle={showSpecificationsToggle}
        queryActionParam={setPLPQueryParam}
        productCardExpanded={setPLPProductCardExpanded}
        unitOfMeasure={unitOfMeasure}
        expandedId={expandedId}
        catalogEntries={catalogEntries}
        showLeftNav
      />,
      {
        isRequired: false,
        onOpen,
        onClose,
        title
      }
    );
  }, [
    addModalWithOptions,
    bucketModelNumberFacet,
    catalogEntries,
    expandedId,
    facets,
    orderBy,
    removeCurrentModal,
    showSpecificationsToggle,
    t,
    unitOfMeasure
  ]);

  const showSpecifications = !isEmpty(facets);
  if (!showSpecifications) {
    return null;
  }

  return isMD ? (
    <ProductListSpecifications
      facets={facets}
      bucketModelNumberFacet={bucketModelNumberFacet}
      showSpecificationsToggle={showSpecificationsToggle}
      queryActionParam={setPLPQueryParam}
      productCardExpanded={setPLPProductCardExpanded}
      unitOfMeasure={unitOfMeasure}
      activeFacets={activeFacets}
      expandedId={expandedId}
    />
  ) : (
    <AlloyButton
      buttonType="ghost"
      size="large"
      onClick={showSpecificationsModal}
      className="text-nowrap py-2 px-0 ms-2"
    >
      {!isEmpty(activeFacets) && (
        <span className="me-2 u-fs--16" data-testid="facets-counter">
          ({activeFacets.length})
        </span>
      )}
      {t('Filter_And_Sort')}
      <FilterIcon className="ms-1" />
    </AlloyButton>
  );
};

export default ProductListSpecificationsContainer;
